import { useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle2, Mail } from "lucide-react";

interface WaitlistFormProps {
  source?: string;
  variant?: "light" | "dark";
}

export function WaitlistForm({ source = "landing", variant = "light" }: WaitlistFormProps) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setState("error");
      setMessage("Please enter a valid email address.");
      return;
    }
    setState("loading");
    setMessage(null);
    const { error } = await supabase.from("waitlist").insert({
      email: trimmed,
      source,
      locale: typeof navigator !== "undefined" ? navigator.language : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
    if (error && !/(duplicate|unique)/i.test(error.message)) {
      setState("error");
      setMessage("Something went wrong. Please try again.");
      return;
    }
    setState("done");
    setMessage("You're on the list. We'll be in touch soon.");
    setEmail("");
  }

  const isDark = variant === "dark";

  if (state === "done") {
    return (
      <div
        role="status"
        className={`flex items-center gap-3 rounded-2xl px-5 py-4 text-sm font-semibold ${
          isDark ? "bg-background/20 text-background" : "bg-secondary/15 text-secondary"
        }`}
      >
        <CheckCircle2 className="size-5 shrink-0" aria-hidden="true" />
        <span>{message}</span>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="w-full max-w-md" noValidate>
      <label htmlFor={`waitlist-${source}`} className="sr-only">
        Email address
      </label>
      <div
        className={`flex items-center gap-2 rounded-2xl border-2 p-1.5 shadow-lg transition-all focus-within:ring-4 ${
          isDark
            ? "bg-background/15 border-background/30 focus-within:ring-background/20"
            : "bg-card border-clay/40 focus-within:ring-primary/20 focus-within:border-primary"
        }`}
      >
        <Mail
          className={`ml-3 size-5 shrink-0 ${isDark ? "text-background/70" : "text-foreground/40"}`}
          aria-hidden="true"
        />
        <input
          id={`waitlist-${source}`}
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          placeholder="you@family.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={state === "loading"}
          className={`flex-1 bg-transparent px-1 py-2.5 text-base outline-none disabled:opacity-50 ${
            isDark
              ? "text-background placeholder:text-background/50"
              : "text-foreground placeholder:text-foreground/40"
          }`}
        />
        <button
          type="submit"
          disabled={state === "loading"}
          className={`shrink-0 rounded-xl px-5 py-2.5 text-sm font-bold transition-all active:translate-y-0.5 disabled:opacity-50 inline-flex items-center gap-2 ${
            isDark
              ? "bg-background text-foreground hover:bg-background/90"
              : "bg-primary text-primary-foreground hover:bg-primary/90 shadow-press"
          }`}
        >
          {state === "loading" ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden="true" /> Joining…
            </>
          ) : (
            "Join waitlist"
          )}
        </button>
      </div>
      {state === "error" && message && (
        <p role="alert" className={`mt-2 text-xs font-semibold ${isDark ? "text-background" : "text-destructive"}`}>
          {message}
        </p>
      )}
      <p className={`mt-2 text-[11px] ${isDark ? "text-background/60" : "text-foreground/45"}`}>
        Early access · no spam · unsubscribe anytime.
      </p>
    </form>
  );
}
